import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from '../http/http.backend-service';
import { 
  FeatureFlagsResponse,
  SingleFeatureResponse,
  FeatureCheckResponse,
  AdminFeaturesListResponse,
  CreateFeatureRequest,
  UpdateFeatureRequest,
  CreateFeatureRuleRequest,
  SetUserOverrideRequest,
  ManageSegmentsRequest,
  ManageFeatureRulesRequest,
  UserFeaturesPreview,
  GetOverridesRequest,
  GetOverridesResponse,
  DeleteOverrideRequest,
  SegmentListResponse,
  SegmentUsersResponse,
  SegmentOperationResponse,
  Segment,
  FeatureAnalyticsRequest,
  FeatureAnalyticsResponse
} from '../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsBackendService {
  private readonly BASE_ADDRESS = '/features';
  private readonly ADMIN_BASE_ADDRESS = '/admin/features';

  constructor(private http: HttpBackendService) {}

  public getAllFeatures(): Promise<FeatureFlagsResponse> {
    return firstValueFrom(
      this.http.get<FeatureFlagsResponse>(`${this.BASE_ADDRESS}`)
    );
  }

  public getFeature(key: string): Promise<SingleFeatureResponse> {
    return firstValueFrom(
      this.http.get<SingleFeatureResponse>(`${this.BASE_ADDRESS}/${key}`)
    );
  }

  public checkFeature(key: string): Promise<FeatureCheckResponse> {
    return firstValueFrom(
      this.http.get<FeatureCheckResponse>(`${this.BASE_ADDRESS}/${key}/check`)
    );
  }

  public createFeature(featureData: CreateFeatureRequest): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/create`, featureData)
    );
  }

  public updateFeature(featureData: UpdateFeatureRequest): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(`${this.ADMIN_BASE_ADDRESS}/update`, featureData)
    );
  }

  public listAllFeatures(): Promise<AdminFeaturesListResponse> {
    return firstValueFrom(
      this.http.get<any>(`${this.ADMIN_BASE_ADDRESS}/list`)
    );
  }

  public listAllFeaturesExtended(params?: { includeArchived?: boolean; includeStats?: boolean }): Promise<AdminFeaturesListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.includeArchived) queryParams.set('includeArchived', 'true');
    if (params?.includeStats) queryParams.set('includeStats', 'true');
    
    const url = queryParams.toString() 
      ? `${this.ADMIN_BASE_ADDRESS}/list?${queryParams}` 
      : `${this.ADMIN_BASE_ADDRESS}/list`;

    return firstValueFrom(
      this.http.get<AdminFeaturesListResponse>(url)
    );
  }

  public createFeatureRule(ruleData: CreateFeatureRuleRequest): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/rules`, ruleData)
    );
  }

  public setUserOverride(overrideData: SetUserOverrideRequest): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/user-override`, overrideData)
    );
  }

  public previewUserFeatures(userId: number): Promise<UserFeaturesPreview> {
    return firstValueFrom(
      this.http.get<any>(`${this.ADMIN_BASE_ADDRESS}/preview-user/${userId}`)
    );
  }

  public manageSegments(segmentData: ManageSegmentsRequest): Promise<SegmentOperationResponse | SegmentListResponse | SegmentUsersResponse> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/segments`, segmentData)
    );
  }

  // Convenient wrapper methods for specific segment operations
  public listSegments(): Promise<SegmentListResponse> {
    return this.manageSegments({ action: 'list_segments' }) as Promise<SegmentListResponse>;
  }

  public createSegment(segmentKey: string, segmentName: string, segmentDescription?: string): Promise<SegmentOperationResponse> {
    return this.manageSegments({ 
      action: 'create_segment', 
      segmentKey, 
      segmentName, 
      segmentDescription 
    }) as Promise<SegmentOperationResponse>;
  }

  public updateSegment(segmentKey: string, segmentName: string, segmentDescription?: string, isActive?: boolean): Promise<SegmentOperationResponse> {
    return this.manageSegments({ 
      action: 'update_segment', 
      segmentKey, 
      segmentName, 
      segmentDescription, 
      isActive 
    }) as Promise<SegmentOperationResponse>;
  }

  public deleteSegment(segmentKey: string, force = false): Promise<SegmentOperationResponse> {
    return this.manageSegments({ 
      action: 'delete_segment', 
      segmentKey, 
      force 
    }) as Promise<SegmentOperationResponse>;
  }

  public addUsersToSegment(segmentKey: string, userIds: number[], expiresAt?: string): Promise<SegmentOperationResponse> {
    return this.manageSegments({ 
      action: 'add_user', 
      segmentKey, 
      userIds, 
      expiresAt 
    }) as Promise<SegmentOperationResponse>;
  }

  public removeUserFromSegment(segmentKey: string, userId: number): Promise<SegmentOperationResponse> {
    return this.manageSegments({ 
      action: 'remove_user', 
      segmentKey, 
      userId 
    }) as Promise<SegmentOperationResponse>;
  }

  public listSegmentUsers(segmentKey: string): Promise<SegmentUsersResponse> {
    return this.manageSegments({ 
      action: 'list_segment_users', 
      segmentKey 
    }) as Promise<SegmentUsersResponse>;
  }

  public invalidateCache(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/invalidate-cache`, {})
    );
  }

  public manageFeatureRules(rulesData: ManageFeatureRulesRequest): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.ADMIN_BASE_ADDRESS}/rules/manage`, rulesData)
    );
  }

  public getAllOverrides(params?: GetOverridesRequest): Promise<GetOverridesResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.featureKey) queryParams.set('featureKey', params.featureKey);
    if (params?.userId) queryParams.set('userId', params.userId.toString());
    if (params?.effect) queryParams.set('effect', params.effect);
    if (params?.includeExpired !== undefined) queryParams.set('includeExpired', params.includeExpired.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    
    const url = queryParams.toString() 
      ? `${this.ADMIN_BASE_ADDRESS}/overrides?${queryParams}` 
      : `${this.ADMIN_BASE_ADDRESS}/overrides`;

    return firstValueFrom(
      this.http.get<GetOverridesResponse>(url)
    );
  }

  public deleteOverride(deleteData: DeleteOverrideRequest): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.ADMIN_BASE_ADDRESS}/overrides`, { body: deleteData })
    );
  }

  public getFeatureAnalytics(params?: FeatureAnalyticsRequest): Promise<FeatureAnalyticsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.featureKey) queryParams.set('featureKey', params.featureKey);
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);
    if (params?.includeUsers !== undefined) queryParams.set('includeUsers', params.includeUsers.toString());
    
    const url = queryParams.toString() 
      ? `${this.ADMIN_BASE_ADDRESS}/analytics?${queryParams}` 
      : `${this.ADMIN_BASE_ADDRESS}/analytics`;

    return firstValueFrom(
      this.http.get<FeatureAnalyticsResponse>(url)
    );
  }
}